import React, { useState } from 'react';
import { Star } from 'react-iconly';

/**
 * StarRating Component
 * 
 * A reusable star rating component that allows users to select
 * a rating from 1-5 stars. Supports both interactive and display modes.
 * 
 * @param {Object} props
 * @param {number} props.rating - Current rating (0-5)
 * @param {function} props.onRatingChange - Callback when rating changes
 * @param {boolean} props.interactive - Whether the rating can be changed
 * @param {string} props.size - Size of stars ('small', 'medium', 'large')
 * @param {string} props.color - Color of filled stars
 * @param {string} props.emptyColor - Color of empty stars
 * @param {boolean} props.showLabel - Whether to show rating text
 */
export function StarRating({
  rating = 0,
  onRatingChange,
  interactive = false,
  size = 'medium',
  color = '#f4a261',
  emptyColor = '#E0E0E0',
  showLabel = false
}) {
  const [hoverRating, setHoverRating] = useState(0);
  
  const sizeMap = {
    small: 16,
    medium: 20,
    large: 24
  };
  
  const starSize = sizeMap[size] || sizeMap.medium;
  
  const handleStarClick = (starRating) => {
    if (interactive && onRatingChange) {
      onRatingChange(starRating);
    }
  };
  
  const handleStarHover = (starRating) => {
    if (interactive) {
      setHoverRating(starRating);
    }
  };
  
  const handleMouseLeave = () => {
    if (interactive) {
      setHoverRating(0);
    }
  };
  
  const displayRating = hoverRating || rating;
  
  const getRatingText = (rating) => {
    switch (rating) {
      case 1: return 'Poor';
      case 2: return 'Fair';
      case 3: return 'Good';
      case 4: return 'Very Good';
      case 5: return 'Excellent';
      default: return '';
    }
  };
  
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <div 
        style={{ 
          display: 'flex', 
          gap: '2px',
          cursor: interactive ? 'pointer' : 'default'
        }}
        onMouseLeave={handleMouseLeave}
      >
        {[1, 2, 3, 4, 5].map((star) => {
          const isFilled = star <= displayRating;
          const isHovered = interactive && star === hoverRating;
          
          return (
            <div
              key={star}
              onClick={() => handleStarClick(star)}
              onMouseEnter={() => handleStarHover(star)}
              style={{
                transition: 'all 0.2s ease',
                transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                cursor: interactive ? 'pointer' : 'default'
              }}
            >
              <Star
                size={starSize}
                color={isFilled ? color : emptyColor}
                filled={isFilled}
              />
            </div>
          );
        })}
      </div>
      
      {showLabel && rating > 0 && (
        <span style={{
          fontSize: '14px',
          color: '#666',
          marginLeft: '8px'
        }}>
          {getRatingText(rating)}
        </span>
      )}
    </div>
  );
}

export default StarRating;
